import { Button, Col, Grid, Input, message, Popconfirm, Row, Space } from "antd";
import { useState } from "react";
import Table, { ColumnsType } from "antd/lib/table";
import { EditOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthContext";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";
import { formatDateRangeString, formatDateString } from "../../Helpers/DateHelpers";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { isOnlyOnWaitlist } from "../../Helpers/WaitlistHelper";
import { ReplyService } from "../../Services/ReplyService";
import { TextService } from "../../Services/TextService";
import DeleteReplyIcon from "./DeleteReplyIcon";
import { compareNames, matchesReplySearch } from "../../Helpers/ReplySearchHelper";

const WAITLIST_ANSWER = 3;

/**
 * The waitlist of an event, in the same layout as the replies: one row for each guest and one column
 * for each time. The cell shows the guest's place in the queue for that time and the button that
 * gives the guest a place. Nobody is moved up automatically.
 */
export const WaitlistTable = (props: any) => {
    const GetText = TextService.GetText;
    const navigation = useNavigate();
    const { user } = useAuth();
    const isMobile = !Grid.useBreakpoint().md;
    const [searchText, setSearchText] = useState("");

    // Opens the guest's reply for editing, the same way as from the list of replies.
    const onEditClick = (authCode: string) => {
        if (user?.subscriptionLevel === SubscriptionLevel.Free) {
            message.warning(GetText("ReplyTable_EditReplyOnlyForPremium"));
            return;
        }

        navigation("/" + getCurrentLanguage() + "/Event/" + props.event.eventUniqueId + "/Reply/" + authCode);
    };

    const getWaitlistedAt = (reply: any, timeId: number): number | undefined => {
        const answer = reply.replyTime?.find((replyTime: any) => replyTime.timeId === timeId && replyTime.customerAnswerType === WAITLIST_ANSWER);
        return answer ? new Date(answer.waitlistedAt ?? 0).getTime() : undefined;
    };

    // The guests waiting for a time, the one who has waited the longest first.
    const getQueue = (timeId: number): any[] => {
        return (props.event.timeAnswers ?? [])
            .filter((reply: any) => getWaitlistedAt(reply, timeId) !== undefined)
            .sort((a: any, b: any) => getWaitlistedAt(a, timeId)! - getWaitlistedAt(b, timeId)!);
    };

    const getFirstWaitlistedAt = (reply: any): number => {
        const times = (reply.replyTime ?? [])
            .filter((replyTime: any) => replyTime.customerAnswerType === WAITLIST_ANSWER)
            .map((replyTime: any) => new Date(replyTime.waitlistedAt ?? 0).getTime());
        return Math.min(...times);
    };

    // Everyone that is on the waitlist for some time, the one who has waited the longest first.
    const guests: any[] = (props.event.timeAnswers ?? [])
        .filter((reply: any) => reply.replyTime?.some((replyTime: any) => replyTime.customerAnswerType === WAITLIST_ANSWER))
        .sort((a: any, b: any) => getFirstWaitlistedAt(a) - getFirstWaitlistedAt(b));

    const onPromote = async (replyId: number, timeId: number) => {
        try {
            await ReplyService.PromoteReply(replyId, timeId);
            message.success(GetText("Waitlist_Promoted"));
        } catch (error: any) {
            message.error(error?.response?.status === 409 ? GetText("Waitlist_NoFreePlace") : GetText("Common_TempError"));
        }

        props.onPromoted();
    };

    const columns: ColumnsType<any> = [
        {
            title: () => { return <><UserOutlined /> {GetText("Waitlist_Title")}</> },
            key: 'customerName',
            // Unsorted the list is in queue order.
            sorter: (a, b) => compareNames(a.customerName, b.customerName),
            // The name stays in place while the times are scrolled sideways on a phone.
            fixed: 'left',
            width: isMobile ? 160 : undefined,
            render: (_, guest) => (
                <Row wrap={false} align="middle" gutter={12}>
                    <Col flex="auto">
                        <b>{guest.customerName}</b>
                        <div className="reply-guest-sub">{guest.customerEmail ? guest.customerEmail : <i>{GetText("Waitlist_NoEmail")}</i>}</div>
                        {guest.customerPhone ? <div className="reply-guest-sub">{guest.customerPhone}</div> : ""}
                        {guest.customerAnswer ? <div className="reply-guest-sub"><i>{guest.customerAnswer}</i></div> : ""}
                    </Col>
                    <Col flex="none">
                        <Space size="middle">
                            <EditOutlined onClick={() => onEditClick(guest.authCode)} />
                            {/* A guest with answers for other times too is removed among the replies, where the whole reply is shown. */}
                            {isOnlyOnWaitlist(guest) ? <DeleteReplyIcon onReplyDelete={props.onReplyDelete} reply={guest} /> : null}
                        </Space>
                    </Col>
                </Row>
            ),
        }
    ];

    props.event.times?.forEach((time: any) => {
        const queue = getQueue(time.id);
        const freePlaces = Math.max((props.event.eventParticipants ?? 0) - (time.yesCount ?? 0), 0);

        columns.push({
            title: () => {
                return <div className="date-header" style={{ cursor: "default" }}>
                    <div className="date-title">{moment(time.start).format("ddd D MMM")}</div>
                    <div className="date-sub">
                        {!time.isFullDay ? <>{formatDateRangeString(time.start, time.end, time.isFullDay, getCurrentLanguage() === "se")} &middot; </> : ""}
                        {GetText("Waitlist_FreePlaces")} {freePlaces}
                    </div>
                </div>;
            },
            key: 'time-' + time.id,
            render: (_, guest) => {
                const position = queue.findIndex((queued: any) => queued.id === guest.id);

                if (position < 0) {
                    return "";
                }

                const waitlistedAt = getWaitlistedAt(guest, time.id);

                return <div className="reply-answer">
                    <Space size="small">
                    <span>{GetText("Waitlist_Position")} <b>{position + 1}</b></span>
                    <Popconfirm
                        title={GetText("Waitlist_PromoteConfirm")}
                        okText={GetText("Waitlist_Promote")}
                        cancelText={GetText("Common_Cancel")}
                        disabled={freePlaces === 0}
                        onConfirm={() => onPromote(guest.id, time.id)}
                    >
                        <Button size="small" type="primary" disabled={freePlaces === 0} title={freePlaces === 0 ? GetText("Waitlist_NoFreePlace") : undefined}>
                            {GetText("Waitlist_Promote")}
                        </Button>
                    </Popconfirm>
                    </Space>
                    {/* Tells the admin how long the guest has waited, which the place in the queue alone doesn't. */}
                    {waitlistedAt ? <div className="reply-guest-sub">{GetText("Waitlist_Since")} {formatDateString(new Date(waitlistedAt))}</div> : ""}
                </div>;
            },
        });
    });

    return <>
        <i>{GetText("Waitlist_Description")}</i>
        {/* The place in the queue is counted on the whole list, so it stays the same while searching. */}
        {guests.length > 0 ?
            <Input.Search allowClear style={{ margin: "10px 0" }} placeholder={GetText("ReplyTable_SearchPlaceholder")} value={searchText} onChange={(e) => setSearchText(e.target.value)} /> : null}
        <Table columns={columns} scroll={{ x: 'max-content' }} className="reply-table" rowKey="id"
            locale={{ emptyText: GetText(guests.length > 0 ? "ReplyTable_NoMatches" : "Waitlist_Empty") }}
            dataSource={guests.filter(guest => matchesReplySearch(guest, searchText))}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, locale: { items_per_page: "/ " + GetText("Common_Page") },hideOnSinglePage: guests.length <= 20, showTotal: (total) => `${total} ${GetText("ReplyTable_Persons")}` }} />
    </>;
};
